import { Link } from 'react-router-dom';
import { Avatar, Badge } from '../../../components/ui';
import { dateFilter, optionsFilter } from '../../../components/common/ListPage/listFilters';
import { ListPage } from '../../../components/common/ListPage/ListPage';
import { ROLES } from '../../../constants/roles';
import { api } from '../../../services/api/apiClient';
import { labelOf } from '../../../utils/labels';
import { ACTIVITY_ACTIONS, ACTIVITY_ENTITIES, ACTIVITY_TABS } from '../constants/activityLabels';
import { ACTIVITY_PATH, activityLink } from '../services/activityService';
import type { ListFilter } from '../../../types/table';
import type { StatusEntry } from '../../../types/common';
import type { Activity } from '../types/activityTypes';

/** "30 Sept 2026, 9:44 am" */
const when = (d: string) =>
  new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const actionOf = (a: Activity): StatusEntry => ACTIVITY_ACTIONS[a.action] ?? ['Changed', 'blue'];

/** Anyone in the team (including the owner). */
const userFilter: ListFilter = {
  key: 'userId',
  label: 'Person',
  icon: 'user',
  fetcher: (search) => api.get('/users', { search, limit: 20 }).then((r) => r.data),
  getDescription: (u) => ROLES[u.role as keyof typeof ROLES] ?? u.role,
};

/** The record name, linked to its screen when it still exists. */
function RecordLink({ a }: { a: Activity }) {
  const to = activityLink(a.entityType, a.entityId, a);
  const text = a.label || (a.entityType === 'account' ? '' : labelOf(ACTIVITY_ENTITIES, a.entityType));
  if (!text) return <span className="text-ink-400">-</span>;
  return to ? (
    <Link to={to} onClick={(e) => e.stopPropagation()} className="font-medium text-brand-600 hover:underline">
      {text}
    </Link>
  ) : (
    <span className="text-navy-900">{text}</span>
  );
}

/** Who changed what, and when. Owner only. */
export default function ActivityList() {
  return (
    <ListPage<Activity>
      title="Activity log"
      subtitle="Every change made in the app: who did it and when. Kept for two years."
      path={ACTIVITY_PATH}
      tabs={ACTIVITY_TABS}
      tabKey="group"
      defaultSort="-at"
      filters={[dateFilter('Date'), userFilter, optionsFilter('entityType', 'Module', ACTIVITY_ENTITIES, 'grid')]}
      searchPlaceholder="Search record, person or change"
      rowTo={(a) => activityLink(a.entityType, a.entityId, a) ?? '/activity'}
      columns={[
        { header: 'When', sort: 'at', sortDesc: true, render: (a) => <span className="whitespace-nowrap">{when(a.at)}</span>, csv: (a) => when(a.at) },
        {
          header: 'Who',
          sort: 'userName',
          render: (a) => (
            <span className="flex items-center gap-2.5">
              <Avatar name={a.userName || '?'} size={28} />
              <span className="min-w-0">
                <span className="block truncate font-medium text-navy-900">{a.userName || 'Unknown'}</span>
                {a.role && <span className="block text-xs text-ink-400">{ROLES[a.role as keyof typeof ROLES] ?? a.role}</span>}
              </span>
            </span>
          ),
          csv: (a) => a.userName,
        },
        {
          header: 'Action',
          sort: 'action',
          render: (a) => {
            const [label, tone] = actionOf(a);
            return <Badge tone={tone} dot>{label}</Badge>;
          },
          csv: (a) => actionOf(a)[0],
        },
        { header: 'What happened', render: (a) => <span className="text-navy-900">{a.summary}</span>, csv: (a) => a.summary },
        { header: 'Record', render: (a) => <RecordLink a={a} />, csv: (a) => a.label },
        { header: 'Module', sort: 'entityType', render: (a) => labelOf(ACTIVITY_ENTITIES, a.entityType), csv: (a) => labelOf(ACTIVITY_ENTITIES, a.entityType), defaultHidden: true },
      ]}
      mobile={(a) => ({
        title: a.summary,
        subtitle: [a.userName, when(a.at)].filter(Boolean).join(' · '),
        badge: <Badge tone={actionOf(a)[1]}>{actionOf(a)[0]}</Badge>,
      })}
      exportName="activity-log"
      empty={{ icon: 'activity', title: 'No activity yet', message: 'Changes made in the app will appear here.' }}
    />
  );
}
